import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchData,
  addWhatsNewAction,
  editWhatsNewAction,
  deleteWhatsNew,
} from "../../redux/slices/mainDataSlice";
import toast from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faPlus,
  faTrashAlt,
  faCodeBranch,
  faParagraph,
  faCheckCircle,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import "../../main.scss";
import Can from "../../permissions/Can";
import Tooltip from "../../components/Tooltip";

const WhatsNew = () => {
  const dispatch = useDispatch();
  const whatsNewData = useSelector(
    (state) =>
      state.main.data?.whatsNewData || state.main.data?.whatsnewtbl || []
  );

  const newprogver = useSelector(
    (state) => state.main.data?.alfaresVersionData?.[0]?.newprogver
  );

  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newVersion, setNewVersion] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [editVersion, setEditVersion] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editId, setEditId] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [animateIn, setAnimateIn] = useState(false);

  const notesRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      if (whatsNewData.length === 0) {
        try {
          await dispatch(fetchData());
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      }
      setLoading(false);
    };
    loadData();
  }, [dispatch, whatsNewData.length]);

  useEffect(() => {
    if (showAddForm || showEditForm) {
      requestAnimationFrame(() => setAnimateIn(true));
    } else {
      setAnimateIn(false);
    }
  }, [showAddForm, showEditForm]);

  const incrementVersion = (version) => {
    if (!version) return "1.0.0";
    const parts = version.split(".");
    if (parts.length === 3) {
      const major = parseInt(parts[0]) || 0;
      const minor = parseInt(parts[1]) || 0;
      const patch = parseInt(parts[2]) || 0;
      if (patch >= 9) return `${major}.${minor + 1}.0`;
      return `${major}.${minor}.${patch + 1}`;
    } else if (parts.length === 2) {
      return `${parseInt(parts[0]) || 0}.${(parseInt(parts[1]) || 0) + 1}`;
    } else {
      return `${(parseInt(parts[0]) || 0) + 1}.0.0`;
    }
  };

  const handleAddNote = () => {
    setNewVersion(incrementVersion(newprogver));
    setNewNotes("");
    setShowAddForm(true);
    setShowEditForm(false);
  };

  const handleEditNote = (id, version, notes) => {
    setEditId(id);
    setEditVersion(version);
    setEditNotes(notes);
    setShowEditForm(true);
    setShowAddForm(false);
  };

  const handleSubmitNewNote = async () => {
    if (!newVersion.trim() || !newNotes.trim()) {
      toast.error("يرجى ملء جميع الحقول");
      return;
    }
    const loadingToast = toast.loading("جارٍ إضافة الملاحظة...");
    try {
      await dispatch(
        addWhatsNewAction({ VerNumber: newVersion, Newnotes: newNotes })
      );
      toast.success("تم إضافة الملاحظة بنجاح", { id: loadingToast });
      setNewVersion("");
      setNewNotes("");
      setShowAddForm(false);
    } catch (error) {
      toast.error("فشل الإضافة", { id: loadingToast });
    }
  };

  const handleSubmitEditNote = async () => {
    if (!editVersion.trim() || !editNotes.trim()) {
      toast.error("يرجى ملء جميع الحقول");
      return;
    }
    const loadingToast = toast.loading("جارٍ تحديث الملاحظة...");
    try {
      await dispatch(
        editWhatsNewAction({
          id: editId,
          VerNumber: editVersion,
          Newnotes: editNotes,
        })
      );
      toast.success("تم تحديث الملاحظة بنجاح", { id: loadingToast });
      setEditVersion("");
      setEditNotes("");
      setEditId(null);
      setShowEditForm(false);
    } catch (error) {
      toast.error("فشل التحديث", { id: loadingToast });
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) return;

    toast(
      (t) => (
        <div className="confirm-toast">
          <p>هل أنت متأكد من حذف {selectedItems.length} ملاحظة؟</p>
          <div className="toast-actions">
            <button
              onClick={async () => {
                const l = toast.loading("جارٍ الحذف...");
                try {
                  for (let id of selectedItems) {
                    await dispatch(deleteWhatsNew(id));
                  }
                  setSelectedItems([]);
                  toast.success("تم الحذف بنجاح");
                } catch {
                  toast.error("فشل الحذف");
                } finally {
                  toast.dismiss(l);
                  toast.dismiss(t.id);
                }
              }}
              className="btn-confirm"
            >
              نعم، احذف
            </button>
            <button onClick={() => toast.dismiss(t.id)} className="btn-cancel">
              إلغاء
            </button>
          </div>
        </div>
      ),
      { duration: 6000 }
    );
  };

  const toggleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const closeModals = () => {
    setShowAddForm(false);
    setShowEditForm(false);
    setEditId(null);
  };

  return (
    <div className="whatsnew-wrapper anim-fade-in">
      <div className="premium-edit-container" style={{ maxWidth: "1280px" }}>
        {/* Premium Header */}
        <div className="premium-header-modern">
          <div className="header-left">
            <div className="header-icon-box whatsnew-icon">
              <FontAwesomeIcon icon={faStar} />
            </div>
            <div className="header-text-box">
              <h2>جديد الإصدارات</h2>
              <div className="breadcrumb-info">
                <span>نظام الفارس</span>
                <span className="separator">/</span>
                <span className="current-page">سجل التغييرات والمستجدات</span>
              </div>
            </div>
          </div>

          <div className="header-tools">
            <Can module="whats_new" action="delete">
              <button
                className={`bulk-delete-btn ${
                  selectedItems.length > 0 ? "show" : ""
                }`}
                onClick={handleDeleteSelected}
              >
                <FontAwesomeIcon icon={faTrashAlt} />
                <span>حذف المحدد ({selectedItems.length})</span>
              </button>
            </Can>

            <Can module="whats_new" action="add">
              <button onClick={handleAddNote} className="premium-action-btn">
                <FontAwesomeIcon icon={faPlus} />
                <span>إضافة ملاحظة جديدة</span>
              </button>
            </Can>
          </div>
        </div>

        {/* Table */}
        <div className="premium-table-container">
          {loading ? (
            <div className="loader-overlay">
              <div className="premium-loader"></div>
              <span>جارٍ تحميل البيانات...</span>
            </div>
          ) : (
            <table className="modern-condensed-table">
              <thead>
                <tr>
                  <th width="30">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked)
                          setSelectedItems(whatsNewData.map((n) => n.ID));
                        else setSelectedItems([]);
                      }}
                      checked={
                        selectedItems.length === whatsNewData.length &&
                        whatsNewData.length > 0
                      }
                    />
                  </th>
                  <th width="30">#</th>
                  <th width="90">الإصدار</th>
                  <th>ملاحظات الإصدار والمستجدات</th>
                  <th width="80" style={{ textAlign: "center" }}>إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {whatsNewData && whatsNewData.length > 0 ? (
                  whatsNewData.map((item, index) => (
                    <tr
                      key={item.ID}
                      className={
                        selectedItems.includes(item.ID) ? "row-selected" : ""
                      }
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.ID)}
                          onChange={() => toggleSelectItem(item.ID)}
                        />
                      </td>
                      <td
                        style={{
                          color: "var(--muted)",
                          fontWeight: 700,
                          textAlign: "center",
                        }}
                      >
                        {index + 1}
                      </td>
                      <td
                        onClick={() =>
                          handleEditNote(item.ID, item.VerNumber, item.Newnotes)
                        }
                        style={{ cursor: "pointer", textAlign: "center" }}
                      >
                        <span className="version-tag">{item.VerNumber}</span>
                      </td>
                      <td
                        className="notes-cell"
                        onClick={() =>
                          handleEditNote(item.ID, item.VerNumber, item.Newnotes)
                        }
                      >
                        <span>{item.Newnotes}</span>
                      </td>
                      <td>
                        <div className="table-actions">
                          <Tooltip content="تعديل">
                            <button
                              className="btn-table btn-table--edit"
                              onClick={() =>
                                handleEditNote(item.ID, item.VerNumber, item.Newnotes)
                              }
                           
                            >
                              <FontAwesomeIcon icon={faParagraph} />
                            </button>
                          </Tooltip>
                          <Tooltip content="حذف">
                            <button
                              className="btn-table btn-table--delete"
                              onClick={() => {
                                setSelectedItems([item.ID]);
                                handleDeleteSelected();
                              }}
                           
                            >
                              <FontAwesomeIcon icon={faTrashAlt} />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="empty-ui-state">
                      <FontAwesomeIcon icon={faStar} className="icon" />
                      <h3>لا توجد ملاحظات حالياً</h3>
                      <p>
                        ابدأ بإضافة أول ملاحظة لسجل الإصدارات من زر الإضافة
                        أعلاه
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Premium Modal for Add / Edit */}
      {(showAddForm || showEditForm) && (
        <div className={`premium-modal-overlay ${animateIn ? "active" : ""}`}>
          <div className="premium-modal-card">
            <div className="modal-header">
              <div className="icon">
                <FontAwesomeIcon icon={showAddForm ? faPlus : faParagraph} />
              </div>
              <h3>{showAddForm ? "إضافة ملاحظة جديدة" : "تعديل الملاحظة"}</h3>
            </div>

            <div className="modal-body">
              {/* رقم الإصدار */}
              <div className="input-field">
                <label>رقم الإصدار</label>
                <div className="input-wrapper">
                  <FontAwesomeIcon icon={faCodeBranch} className="field-icon" />
                  <input
                    type="text"
                    value={showAddForm ? newVersion : editVersion}
                    onChange={(e) =>
                      showAddForm
                        ? setNewVersion(e.target.value)
                        : setEditVersion(e.target.value)
                    }
                    placeholder="مثال: 3.2.1"
                    dir="ltr"
                    style={{
                      textAlign: "center",
                      fontWeight: 700,
                      fontSize: "16px",
                      color: "#C5172E",
                    }}
                  />
                </div>
              </div>

              {/* الملاحظات */}
              <div className="input-field">
                <label>ملاحظات الإصدار</label>
                <div className="input-wrapper">
                  <FontAwesomeIcon
                    icon={faParagraph}
                    className="field-icon"
                    style={{ top: "15px" }}
                  />
                  <textarea
                    ref={showAddForm ? notesRef : null}
                    value={showAddForm ? newNotes : editNotes}
                    onChange={(e) =>
                      showAddForm
                        ? setNewNotes(e.target.value)
                        : setEditNotes(e.target.value)
                    }
                    placeholder="اكتب تفاصيل مستجدات هذا الإصدار هنا..."
                    style={{
                      height: "140px",
                      paddingTop: "12px",
                      textAlign: "justify",
                      lineHeight: "1.8",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={
                  showAddForm ? handleSubmitNewNote : handleSubmitEditNote
                }
                className="premium-submit-btn"
              >
                <div className="btn-content">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>{showAddForm ? "نشر الملاحظة" : "تحديث البيانات"}</span>
                </div>
                <div className="btn-glow"></div>
              </button>
              <button onClick={closeModals} className="cancel-btn">
                <FontAwesomeIcon icon={faTimesCircle} />
                <span>إلغاء</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
                .whatsnew-wrapper { padding: 10px; color: var(--text); font-family: 'Cairo', sans-serif; }
                .premium-edit-container {
                    margin: 0;
                    background: transparent;
                    border: none;
                    border-radius: 0;
                    overflow: visible;
                    box-shadow: none;
                    width: 100%;
                }

                /* Header */
                .premium-header-modern {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 15px 25px;
                    background: rgba(255, 255, 255, 0.7);
                    backdrop-filter: blur(10px);
                    border: 1px solid var(--border);
                    border-radius: 12px 12px 0 0;
                    gap: 20px;
                }
                
                .header-icon-box {
                    width: 44px; height: 44px; background: var(--accent);
                    border-radius: 10px; display: flex; align-items: center; justify-content: center;
                    font-size: 18px; color: white;
                    box-shadow: 0 8px 16px -4px color-mix(in srgb, var(--accent) 40%, transparent);
                }
                .whatsnew-icon { background: linear-gradient(135deg, #d97706, #b45309); }
                .header-text-box h2 { margin: 0; font-size: 18px; font-weight: 800; }
                .breadcrumb-info { display: flex; align-items: center; gap: 4px; color: var(--muted); font-size: 11px; margin-top: 2px; }
                .breadcrumb-info span:first-child { color: #d97706; font-weight: 500; }
                .separator { margin: 0 4px; }
                .current-page { color: #d97706; font-weight: 600; }

                .header-tools { display: flex; align-items: center; gap: 12px; }
                .premium-action-btn {
                    background: var(--accent); color: white; border: none; border-radius: 10px;
                    padding: 8px 16px; display: flex; align-items: center; gap: 8px;
                    font-size: 13px; font-weight: 700; cursor: pointer; transition: 0.3s;
                    box-shadow: 0 8px 16px -4px color-mix(in srgb, var(--accent) 40%, transparent);
                    font-family: 'Cairo', sans-serif; white-space: nowrap;
                }
                .premium-action-btn:hover { transform: translateY(-2px); filter: brightness(1.1); }

                .bulk-delete-btn {
                    background: #ef4444; color: white; border: none; border-radius: 10px;
                    display: flex; align-items: center; gap: 10px;
                    font-size: 13px; font-weight: 700; cursor: pointer; font-family: 'Cairo', sans-serif;
                    opacity: 0; pointer-events: none; transform: scale(0.8); transition: all 0.3s;
                    max-width: 0; padding: 0; margin: 0; overflow: hidden; white-space: nowrap;
                }
                .bulk-delete-btn.show { 
                    opacity: 1; pointer-events: auto; transform: scale(1); 
                    max-width: 300px; padding: 8px 16px;
                }

                /* Table Section (Matching EmployeeList Style) */
                .premium-table-container { 
                    padding: 4px; /* Tiny padding for more grid feel */
                    background: var(--panel);
                    border: 1px solid var(--border);
                    border-top: none;
                    border-radius: 0 0 12px 12px;
                    overflow-x: auto;
                    box-shadow: var(--shadow-sm);
                }
                
                .modern-condensed-table { 
                    width: 100%; 
                    border-collapse: collapse; /* Like Excel */
                    text-align: right;
                    font-size: 13px;
                    table-layout: auto;
                }

                .modern-condensed-table th { 
                    background: var(--panel-2); 
                    padding: 12px 10px; /* Reduced horiz padding to narrow columns */
                    text-align: center; 
                    color: var(--accent); 
                    font-weight: 700; 
                    font-size: 13px;
                    border: 1px solid var(--border);
                    white-space: nowrap;
                }
                
                /* Left-align text for the notes column header */
                .modern-condensed-table th:nth-child(4) {
                    text-align: right;
                }

                .modern-condensed-table td { 
                    padding: 8px 10px; /* Reduced horiz padding to narrow columns */
                    border: 1px solid var(--border);
                    color: var(--text);
                    transition: 0.2s;
                    background: transparent;
                }

                .modern-condensed-table tbody tr:hover { 
                    background: rgba(255,255,255,0.03); 
                }
                
                .modern-condensed-table tr.row-selected { 
                    background: rgba(239, 68, 68, 0.05) !important; 
                }

                .version-tag {
                    font-weight: 700; font-size: 15px; color: #C5172E; direction: ltr;
                    letter-spacing: 0.5px;
                }

                .notes-cell { cursor: pointer; }
                .notes-cell span {
                    font-size: 13.5px; color: var(--text); line-height: 1.6;
                    white-space: pre-wrap; word-break: break-word;
                    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
                    overflow: hidden;
                    font-weight: 400;
                }
                .notes-cell:hover span { color: var(--accent); }

                .table-actions {
                    display: flex;
                    gap: 6px;
                    justify-content: center;
                }

                .btn-table {
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 8px;
                    border: 1px solid var(--border);
                    background: var(--panel-2);
                    cursor: pointer;
                    transition: 0.2s;
                    font-size: 14px;
                    color: var(--muted);
                }

                .btn-table--edit:hover { 
                    background: var(--accent); 
                    color: white; 
                    border-color: var(--accent); 
                }

                .btn-table--delete:hover { 
                    background: var(--danger); 
                    color: white; 
                    border-color: var(--danger); 
                }

                /* Modal Styling */
                .premium-modal-overlay {
                    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
                    background: rgba(13, 35, 32, 0.45); backdrop-filter: blur(10px);
                    display: flex; align-items: center; justify-content: center; z-index: 10000;
                    opacity: 0; pointer-events: none; transition: 0.4s;
                }
                .premium-modal-overlay.active { opacity: 1; pointer-events: auto; }
                .premium-modal-card {
                    background: var(--panel); border: 1px solid var(--border); width: 100%; max-width: 560px;
                    border-radius: 20px; padding: 30px;
                    transform: translateY(30px) scale(0.95); transition: 0.4s;
                    box-shadow: 0 40px 100px -20px rgba(0,0,0,0.6);
                }
                .premium-modal-overlay.active .premium-modal-card { transform: translateY(0) scale(1); }

                .modal-header { display: flex; align-items: center; gap: 20px; margin-bottom: 25px; }
                .modal-header .icon {
                    width: 50px; height: 50px; background: rgba(217, 119, 6, 0.12);
                    border-radius: 12px; display: flex; align-items: center; justify-content: center;
                    font-size: 22px; color: #d97706;
                }
                .modal-header h3 { margin: 0; font-size: 20px; font-weight: 800; }

                .input-field { margin-bottom: 20px; }
                .input-field label { display: block; font-size: 13px; font-weight: 700; color: var(--muted); margin-bottom: 10px; margin-right: 5px; }
                .input-wrapper { position: relative; display: flex; align-items: center; }
                .field-icon { position: absolute; right: 18px; color: var(--muted); font-size: 16px; opacity: 0.6; z-index: 1; pointer-events: none; }
                .input-wrapper input, .input-wrapper textarea {
                    width: 100%; height: 48px; background: var(--panel-2); border: 1px solid var(--border);
                    border-radius: 10px; padding: 0 48px 0 20px; color: var(--text); font-family: 'Cairo', inherit;
                    font-size: 15px; transition: 0.3s; outline: none;
                }
                .input-wrapper textarea { height: auto; padding-top: 15px; }
                .input-wrapper input:focus, .input-wrapper textarea:focus {
                    border-color: #d97706; box-shadow: 0 0 0 4px rgba(217,119,6,0.1);
                }

                .modal-footer { display: flex; gap: 15px; margin-top: 30px; }
                .premium-submit-btn {
                    flex: 2; background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
                    border: none; color: white; height: 52px; border-radius: 10px;
                    font-size: 16px; font-weight: 700; cursor: pointer; position: relative;
                    overflow: hidden; transition: 0.3s; font-family: 'Cairo', sans-serif;
                }
                .btn-content { position: relative; z-index: 2; display: flex; align-items: center; justify-content: center; gap: 10px; }
                .btn-glow { position: absolute; top: -50%; left: -50%; width: 200%; height: 200%; background: radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%); opacity: 0; transition: 0.3s; }
                .premium-submit-btn:hover { transform: translateY(-2px); filter: brightness(1.1); }
                .premium-submit-btn:hover .btn-glow { opacity: 1; }
                .cancel-btn {
                    flex: 1; background: var(--panel-2); border: 1px solid var(--border);
                    color: var(--text); border-radius: 10px; display: flex; align-items: center;
                    justify-content: center; gap: 10px; font-size: 15px; font-weight: 700; cursor: pointer;
                    font-family: 'Cairo', sans-serif; transition: 0.2s;
                }
                .cancel-btn:hover { border-color: #ef4444; color: #ef4444; }

                /* Loader & Empty State */
                .loader-overlay { padding: 60px; text-align: center; color: var(--muted); display: flex; flex-direction: column; gap: 20px; align-items: center; }
                .premium-loader { width: 40px; height: 40px; border: 3px solid rgba(217, 119, 6, 0.1); border-top-color: #d97706; border-radius: 50%; animation: spin 1s infinite linear; }
                @keyframes spin { to { transform: rotate(360deg); } }

                .empty-ui-state { text-align: center; padding: 100px !important; color: var(--muted); border: none !important; }
                .empty-ui-state .icon { font-size: 60px; opacity: 0.2; margin-bottom: 25px; display: block; }
                .empty-ui-state h3 { font-size: 22px; font-weight: 800; margin: 0; color: var(--text); }

                @media (max-width: 900px) {
                    .premium-header-modern { padding: 12px 15px; border-radius: 12px 12px 0 0; }
                    .header-icon-box { width: 36px; height: 36px; font-size: 16px; }
                    .header-text-box h2 { font-size: 16px; }
                    .premium-action-btn, .bulk-delete-btn { padding: 7px 12px; font-size: 12px; }
                }
            `}</style>
    </div>
  );
};

export default WhatsNew;
